/**
 * PromotedCardDropdown component will expand and collapse to view Don't miss Rewards banner.
 *
 * @module views/components/PromotedCardDropdown
 * @memberof -Common
 */
import './PromotedCardDropdown.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Accordion from '@ulta/core/components/Accordion/Accordion';
import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Tooltip from '@ulta/core/components/Tooltip/Tooltip';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Button from '@ulta/modules/Button/Button';
import Markdown from '@ulta/modules/Markdown/Markdown';
import Text from '@ulta/modules/Text/Text';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';

/**
  * Represents an PromotedCardDropdown component
  * @method
  * @param {PromotedCardDropdownProps} props - React properties passed from composition
  * @returns PromotedCardDropdown
  */
export const PromotedCardDropdown = function( props ){
  const {
    action,
    elements,
    headline,
    icon,
    subheadline,
    message,
    toolTip,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;

  const componentRef = useRef();
  useIntersectionObserver(
    componentRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  const { breakpoint } = useDeviceInflection();
  const isLargeDevice = breakpoint.isLargeDevice();

  const hasElements = hasItems( elements );

  const headerChildren = (
    <>
      <Icon name={ icon }
        size='xl'
      />
      <Text htmlTag={ headline?.htmlTag || 'h2' }
        style={ isLargeDevice ? 'title' : 'body-1' }
      >
        { headline?.text }
      </Text>
    </>
  );

  const isAuth0Link = isAuthButton( action );
  return (
    <div className='PromotedCardDropdown'
      ref={ componentRef }
    >
      <Accordion
        headerChildren={ headerChildren }
        isActive
        ariaControls='accordion-section-id'
        ariaLabelledBy='accordion-label-id'
      >
        <div className='pal-c-Accordion__PointsWrapper'>
          <div className='pal-c-Accordion__PointsToEarn'>
            <div className='pal-c-Accordion__GetPoints'>
              { subheadline && (
                <Markdown
                  bodyStyle='body-2'
                  cmsMarkdownContent={ subheadline }
                />
              ) }
              { toolTip?.message && (
                <Tooltip
                  { ...spreadProps( { ...toolTip } ) }
                />
              ) }
            </div>
            { message && (
              <Text htmlTag='p'
                style='body'
                color='neutral-700'
              >
                { message }
              </Text>
            ) }
          </div>
          { hasElements && (
            <ul className='pal-c-Accordion__PointsSection'>
              { elements?.map( ( { label, value }, index ) => (
                <React.Fragment key={ index }>
                  <li className='pal-c-Accordion__PointsBreakdown'>
                    <Text htmlTag='p'
                      style='body-sm'
                    >
                      { label }
                    </Text>
                    <Text htmlTag='p'
                      style='title-6'
                      className='pal-c-Accordion__FancyBackground'
                    >
                      { value }
                    </Text>
                  </li>
                </React.Fragment>
              ) ) }
            </ul>
          ) }
        </div>
        {
          !isAuth0Link &&
          <Button label={ action?.label }
            variant='secondary'
            size='compact'
            type='submit'
            action={ action }
          />
        }

        {
          isAuth0Link &&
          <Link_Huge
            likeButtonSecondary
            compact
            action={ action }
          >
            { action.label }
          </Link_Huge>
        }

      </Accordion>
    </div>
  );
};

/**
  * Property type definitions
  * @typedef PromotedCardDropdownProps
  * @type {object}
  * @property {object} action - Sets the ctaAction
  * @property {array} elements - Sets the elements array for displaying value off details
  * @property {object} headline - Sets the headline textlite
  * @property {string} icon - Sets the icon to dictate what icon to display
  * @property {string} subheadline - Sets the subheadline string for markdown
  * @property {string} message - Sets the message string
  * @property {object} toolTip - Sets the toolTip content
  */
export const propTypes = {
  /** Component content (JSX or string literals) */
  action: PropTypes.object,
  elements: PropTypes.arrayOf( PropTypes.shape( {
    label: PropTypes.string,
    value: PropTypes.string
  } ) ),
  headline: PropTypes.shape( {
    htmlTag: PropTypes.string,
    text: PropTypes.string
  } ),
  icon: PropTypes.string,
  subheadline: PropTypes.string,
  message: PropTypes.string,
  toolTip: PropTypes.shape( {
    accessibilityLabel:PropTypes.string,
    actionIcon:PropTypes.string,
    icon:PropTypes.string,
    message:PropTypes.string
  } ),
  root: PropTypes.object,
  rootMargin: PropTypes.string,
  threshold: PropTypes.number
};

/**
* Default values for passed properties
* @type {object}
*/
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

PromotedCardDropdown.propTypes = propTypes;
PromotedCardDropdown.defaultProps = defaultProps;
export default PromotedCardDropdown;